import { AlignmentLineType, RulerLineType, SingleLineType, WallType, WallConnectionStart, WallConnectionEnd } from "../../types/floorplannerTypes";
import { floorplannerStore } from "../../store/floorplannerStore";
import { renderStore } from "../../store/renderStore";
import { editorStore } from "../../store/editorStore";
import * as THREE from "three";
import { CANVAS_ACCENT_COLOR } from "../../constants/brandColors";

const tolerance = 0.001; // Sensitivity for alignment
const toleranceApprox = 0.1; // Sensitivity for approximate alignment used for snapping
const LINE_DEDUP_TOLERANCE = 0.075; // Tolerance for considering two alignment lines as duplicates (75mm = 3/4 wall width)

type AlignmentPoint = { pos: THREE.Vector2; label: string; allowX: boolean; allowY: boolean };

// Track added alignment lines to prevent duplicates within tolerance
// Now stores the full line object to enable origin-based prioritization
const addedLinesX = new Map<string, AlignmentLineType>(); // key: lineId, value: line object
const addedLinesY = new Map<string, AlignmentLineType>();

// Current drag point for distance comparisons - set before each dedup batch
let currentDragPoint: { x: number; y: number } | null = null;

/**
 * Set the current drag point for origin-based prioritization
 * Should be called at the start of each alignment line update cycle
 */
function setDragPoint(x: number, y: number): void {
    currentDragPoint = { x, y };
}

/**
 * Calculate distance from a point to the drag point
 */
function distanceToOrigin(origin: { x: number; y: number } | undefined): number {
    if (!origin || !currentDragPoint) return Infinity;
    const dx = origin.x - currentDragPoint.x;
    const dy = origin.y - currentDragPoint.y;
    return Math.sqrt(dx * dx + dy * dy);
}

/**
 * Find an existing alignment line within tolerance on the same axis
 * @param axis - "x" or "y"
 * @param position - The position value
 * @returns The existing line if found within tolerance, null otherwise
 */
function findDuplicateLine(axis: "x" | "y", position: number): AlignmentLineType | null {
    const map = axis === "x" ? addedLinesX : addedLinesY;

    // Check if any existing line is within tolerance
    for (const [, existingLine] of map.entries()) {
        const existingPos = existingLine.axis === "x" ? existingLine.hit.x : existingLine.hit.y;
        if (Math.abs(existingPos - position) < LINE_DEDUP_TOLERANCE) {
            return existingLine;
        }
    }

    return null;
}

/**
 * Add a line to the duplicate tracking map
 */
function registerLine(axis: "x" | "y", line: AlignmentLineType): void {
    const map = axis === "x" ? addedLinesX : addedLinesY;
    map.set(line.id, line);
}

/**
 * Remove a line from the duplicate tracking map
 */
function unregisterLine(axis: "x" | "y", lineId: string): void {
    const map = axis === "x" ? addedLinesX : addedLinesY;
    map.delete(lineId);
}

/**
 * Helper to add an alignment line with automatic deduplication and origin-based prioritization.
 * When two lines are within LINE_DEDUP_TOLERANCE on the same axis, the line whose origin point
 * is closer to the current drag point wins.
 */
function addAlignmentLineWithDedup(line: AlignmentLineType, approx: boolean, mode: "center" | "edge" = "center"): void {
    const position = line.axis === "x" ? line.hit.x : line.hit.y;
    line.mode = mode;

    const existingLine = findDuplicateLine(line.axis, position);
    
    if (existingLine) {
        // Compare origin distances - line with closer origin wins
        const existingDistance = distanceToOrigin(existingLine.originPoint);
        const newDistance = distanceToOrigin(line.originPoint);
        
        if (newDistance < existingDistance) {
            // New line has closer origin - replace the existing one
            unregisterLine(line.axis, existingLine.id);
            // Remove old line from renderStore (clear and re-add will handle this)
            renderStore.addAlignmentLine(line, approx);
            registerLine(line.axis, line);
        }
        // Otherwise keep existing line (it has closer or equal origin)
    } else {
        // No duplicate found - add the new line
        renderStore.addAlignmentLine(line, approx);
        registerLine(line.axis, line);
    }
}

/**
 * Calculate edge points for a wall at start and end positions
 * Returns left and right edge points at both start and end
 */
function calculateWallEdgePoints(wall: WallType): {
    startLeft: THREE.Vector2;
    startRight: THREE.Vector2;
    endLeft: THREE.Vector2;
    endRight: THREE.Vector2;
} {
    const wallDirection = new THREE.Vector2(
        wall.end.x - wall.start.x,
        wall.end.y - wall.start.y
    ).normalize();

    // Calculate perpendicular vector (90° rotation)
    const perpendicular = new THREE.Vector2(-wallDirection.y, wallDirection.x);

    // Get wall width (use wall.wallWidth or floorplannerStore default)
    const width = wall.wallWidth || floorplannerStore.wallWidth;
    const halfWidth = width / 2;

    // Calculate edge points
    const startLeft = new THREE.Vector2(
        wall.start.x + perpendicular.x * halfWidth,
        wall.start.y + perpendicular.y * halfWidth
    );
    const startRight = new THREE.Vector2(
        wall.start.x - perpendicular.x * halfWidth,
        wall.start.y - perpendicular.y * halfWidth
    );
    const endLeft = new THREE.Vector2(
        wall.end.x + perpendicular.x * halfWidth,
        wall.end.y + perpendicular.y * halfWidth
    );
    const endRight = new THREE.Vector2(
        wall.end.x - perpendicular.x * halfWidth,
        wall.end.y - perpendicular.y * halfWidth
    );

    return { startLeft, startRight, endLeft, endRight };
}

function getAlignmentTargetPoints(otherWall: WallType | SingleLineType | RulerLineType): AlignmentPoint[] {
    if (otherWall.type !== 'wall') {
        return [
            { pos: otherWall.start.clone(), label: 'other-start', allowX: true, allowY: true },
            { pos: otherWall.end.clone(), label: 'other-end', allowX: true, allowY: true }
        ];
    }

    const otherWallTyped = otherWall as WallType;
    const otherWallEdges = calculateWallEdgePoints(otherWallTyped);

    const isStartConnected =
        otherWallTyped.centerConnections?.some(c => c.sourcePosition === WallConnectionStart) ||
        otherWallTyped.edgeConnections?.some(c => c.sourceEndpoint === "start");

    const isEndConnected =
        otherWallTyped.centerConnections?.some(c => c.sourcePosition === WallConnectionEnd) ||
        otherWallTyped.edgeConnections?.some(c => c.sourceEndpoint === "end");

    const dx = Math.abs(otherWallTyped.end.x - otherWallTyped.start.x);
    const dy = Math.abs(otherWallTyped.end.y - otherWallTyped.start.y);
    const isHorizontal = dy < 0.1; // tolerance for axis alignment
    const isVertical = dx < 0.1;

    let startAllowX = true;
    let startAllowY = true;
    if (isStartConnected) {
        if (isHorizontal) startAllowX = false; // Suppress vertical cap (X-axis)
        else if (isVertical) startAllowY = false; // Suppress horizontal cap (Y-axis)
        else { startAllowX = false; startAllowY = false; }
    }

    let endAllowX = true;
    let endAllowY = true;
    if (isEndConnected) {
        if (isHorizontal) endAllowX = false;
        else if (isVertical) endAllowY = false;
        else { endAllowX = false; endAllowY = false; }
    }

    return [
        { pos: otherWallEdges.startLeft, label: 'other-start-left', allowX: startAllowX, allowY: startAllowY },
        { pos: otherWallEdges.startRight, label: 'other-start-right', allowX: startAllowX, allowY: startAllowY },
        { pos: otherWallEdges.endLeft, label: 'other-end-left', allowX: endAllowX, allowY: endAllowY },
        { pos: otherWallEdges.endRight, label: 'other-end-right', allowX: endAllowX, allowY: endAllowY }
    ];
}

function findAlignmentLines(
    wall: WallType | SingleLineType | RulerLineType,
    otherWall: WallType | SingleLineType | RulerLineType,
    newX: number,
    newY: number,
    dragHandle: string,
) {
    if (otherWall.id !== wall?.id) {
        // Determine if we should use edge points or center points
        // Edge points are used by default; center points when ALT is pressed
        const useCenterPoints = editorStore?.isAltPressed || false;

        // Edge-based alignment mode (default, unless ALT is pressed)
        // This runs for ANY dragged object type when targeting walls
        // For non-walls, we use start/end points; for walls, we use edge corners
        if (!useCenterPoints) {
            // Get comparison points for the dragged object
            let draggedPoints: AlignmentPoint[];
            if (wall.type === 'wall') {
                const wallEdges = calculateWallEdgePoints(wall as WallType);
                draggedPoints = [
                    { pos: wallEdges.startLeft, label: 'start-left', allowX: true, allowY: true },
                    { pos: wallEdges.startRight, label: 'start-right', allowX: true, allowY: true },
                    { pos: wallEdges.endLeft, label: 'end-left', allowX: true, allowY: true },
                    { pos: wallEdges.endRight, label: 'end-right', allowX: true, allowY: true }
                ];
            } else {
                // For non-wall objects (SingleLine, RulerLine), use start/end points
                draggedPoints = [
                    { pos: wall.start.clone(), label: 'start', allowX: true, allowY: true },
                    { pos: wall.end.clone(), label: 'end', allowX: true, allowY: true }
                ];
            }

            // Get target points - use edge points for walls, center points for non-walls
            const otherPoints = getAlignmentTargetPoints(otherWall);

            // Check alignments between dragged object points and target points
            for (const dragPoint of draggedPoints) {
                for (const otherPoint of otherPoints) {
                    // Check X alignment
                    if (otherPoint.allowX && Math.abs(dragPoint.pos.x - otherPoint.pos.x) < toleranceApprox) {
                        addAlignmentLineWithDedup({
                            id: `align-x-edge-${dragPoint.label}-${otherPoint.label}-${otherWall.id}`,
                            hit: { x: otherPoint.pos.x, y: 0 },
                            axis: "x",
                            color: CANVAS_ACCENT_COLOR,
                            lineWidth: 0.5,
                            objectId: otherWall.id,
                            originPoint: { x: otherPoint.pos.x, y: otherPoint.pos.y },
                        }, true, "edge");
                    }

                    // Check Y alignment
                    if (otherPoint.allowY && Math.abs(dragPoint.pos.y - otherPoint.pos.y) < toleranceApprox) {
                        addAlignmentLineWithDedup({
                            id: `align-y-edge-${dragPoint.label}-${otherPoint.label}-${otherWall.id}`,
                            hit: { x: 0, y: otherPoint.pos.y },
                            axis: "y",
                            color: CANVAS_ACCENT_COLOR,
                            lineWidth: 0.5,
                            objectId: otherWall.id,
                            originPoint: { x: otherPoint.pos.x, y: otherPoint.pos.y },
                        }, true, "edge");
                    }
                }
            }
        }

        // Center-based guideline logic - ONLY when ALT is pressed
        // Shows alignment lines at wall center points (start/end) instead of edges
        if (useCenterPoints) {
            // In center mode (ALT pressed), we show all center alignment lines - no filtering
            // For non-wall objects, both endpoints are always visible
            // Note: We intentionally do NOT filter embedded endpoints in center mode
            // because user can still align to the logical center of walls even if
            // the endpoint is visually embedded in another wall
            const otherStartVisible = true;
            const otherEndVisible = true;


            if (
                dragHandle === "wall" ||
                dragHandle === "middleV" ||
                dragHandle === "middleH"
            ) {
                // Align both start and end
                const wallStartX = wall.start.x;
                const wallStartY = wall.start.y;
                const wallEndX = wall.end.x;
                const wallEndY = wall.end.y;


                const alignedXStart =
                    (otherStartVisible && Math.abs(otherWall.start.x - wallStartX) <= tolerance) ||
                    (otherEndVisible && Math.abs(otherWall.end.x - wallStartX) <= tolerance);
                const alignedYStart =
                    (otherStartVisible && Math.abs(otherWall.start.y - wallStartY) <= tolerance) ||
                    (otherEndVisible && Math.abs(otherWall.end.y - wallStartY) <= tolerance);


                if (alignedXStart) {
                    addAlignmentLineWithDedup({
                        id: `align-x-start-${otherWall.id}`,
                        hit: { x: wallStartX, y: 0 },
                        axis: "x",
                        color: CANVAS_ACCENT_COLOR,
                        lineWidth: 0.5,
                        objectId: otherWall.id,
                        originPoint: { x: wallStartX, y: wall.start.y },
                    }, false, "center");
                }
                const alignedXStartStartApprox = otherStartVisible && Math.abs(otherWall.start.x - wallStartX) <= toleranceApprox
                if (alignedXStartStartApprox) {
                    addAlignmentLineWithDedup({
                        id: `align-x-approx-start-start-${otherWall.id}`,
                        hit: { x: otherWall.start.x, y: 0 },
                        axis: "x",
                        color: CANVAS_ACCENT_COLOR,
                        lineWidth: 0.5,
                        objectId: otherWall.id,
                        originPoint: { x: otherWall.start.x, y: otherWall.start.y },
                    }, true, "center");
                }
                const alignedXStartEndApprox = otherEndVisible && Math.abs(otherWall.end.x - wallStartX) <= toleranceApprox;
                if (alignedXStartEndApprox) {
                    addAlignmentLineWithDedup({
                        id: `align-x-approx-start-end-${otherWall.id}`,
                        hit: { x: otherWall.end.x, y: 0 },
                        axis: "x",
                        color: CANVAS_ACCENT_COLOR,
                        lineWidth: 0.5,
                        objectId: otherWall.id,
                        originPoint: { x: otherWall.end.x, y: otherWall.end.y },
                    }, true, "center");
                }

                if (alignedYStart) {
                    addAlignmentLineWithDedup({
                        id: `align-y-start-${otherWall.id}`,
                        hit: { x: 0, y: wallStartY },
                        axis: "y",
                        color: CANVAS_ACCENT_COLOR,
                        lineWidth: 0.5,
                        objectId: otherWall.id,
                        originPoint: { x: wall.start.x, y: wallStartY },
                    }, false, "center");
                }
                const alignedYStartStartApprox = otherStartVisible && Math.abs(otherWall.start.y - wallStartY) <= toleranceApprox
                if (alignedYStartStartApprox) {
                    addAlignmentLineWithDedup({
                        id: `align-y-approx-start-start-${otherWall.id}`,
                        hit: { x: 0, y: otherWall.start.y },
                        axis: "y",
                        color: CANVAS_ACCENT_COLOR,
                        lineWidth: 0.5,
                        objectId: otherWall.id,
                        originPoint: { x: otherWall.start.x, y: otherWall.start.y },
                    }, true, "center");
                }
                const alignedYStartEndApprox = otherEndVisible && Math.abs(otherWall.end.y - wallStartY) <= toleranceApprox;
                if (alignedYStartEndApprox) {
                    addAlignmentLineWithDedup({
                        id: `align-y-approx-start-end-${otherWall.id}`,
                        hit: { x: 0, y: otherWall.end.y },
                        axis: "y",
                        color: CANVAS_ACCENT_COLOR,
                        lineWidth: 0.5,
                        objectId: otherWall.id,
                        originPoint: { x: otherWall.end.x, y: otherWall.end.y },
                    }, true, "center");
                }

                const alignedXEnd =
                    (otherStartVisible && Math.abs(otherWall.start.x - wallEndX) < tolerance) ||
                    (otherEndVisible && Math.abs(otherWall.end.x - wallEndX) < tolerance);
                const alignedYEnd =
                    (otherStartVisible && Math.abs(otherWall.start.y - wallEndY) < tolerance) ||
                    (otherEndVisible && Math.abs(otherWall.end.y - wallEndY) < tolerance);

                if (alignedXEnd) {
                    addAlignmentLineWithDedup({
                        id: `align-x-end-${otherWall.id}`,
                        hit: { x: wallEndX, y: 0 },
                        axis: "x",
                        color: CANVAS_ACCENT_COLOR,
                        lineWidth: 0.5,
                        objectId: otherWall.id,
                        originPoint: { x: wallEndX, y: wall.end.y },
                    }, false, "center");
                }
                const alignedXEndStartApprox = otherStartVisible && Math.abs(otherWall.start.x - wallEndX) < toleranceApprox
                if (alignedXEndStartApprox) {
                    addAlignmentLineWithDedup({
                        id: `align-x-approx-end-start-${otherWall.id}`,
                        hit: { x: otherWall.start.x, y: 0 },
                        axis: "x",
                        color: CANVAS_ACCENT_COLOR,
                        lineWidth: 0.5,
                        objectId: otherWall.id,
                        originPoint: { x: otherWall.start.x, y: otherWall.start.y },
                    }, true, "center");
                }
                const alignedXEndEndApprox = otherEndVisible && Math.abs(otherWall.end.x - wallEndX) < toleranceApprox;
                if (alignedXEndEndApprox) {
                    addAlignmentLineWithDedup({
                        id: `align-x-approx-end-end-${otherWall.id}`,
                        hit: { x: otherWall.end.x, y: 0 },
                        axis: "x",
                        color: CANVAS_ACCENT_COLOR,
                        lineWidth: 0.5,
                        objectId: otherWall.id,
                        originPoint: { x: otherWall.end.x, y: otherWall.end.y },
                    }, true, "center");
                }

                if (alignedYEnd) {
                    addAlignmentLineWithDedup({
                        id: `align-y-end-${otherWall.id}`,
                        hit: { x: 0, y: wallEndY },
                        axis: "y",
                        color: CANVAS_ACCENT_COLOR,
                        lineWidth: 0.5,
                        objectId: otherWall.id,
                        originPoint: { x: wall.end.x, y: wallEndY },
                    }, false, "center");
                }
                const alignedYEndStartApprox = otherStartVisible && Math.abs(otherWall.start.y - wallEndY) < toleranceApprox
                if (alignedYEndStartApprox) {
                    addAlignmentLineWithDedup({
                        id: `align-y-approx-end-start-${otherWall.id}`,
                        hit: { x: 0, y: otherWall.start.y },
                        axis: "y",
                        color: CANVAS_ACCENT_COLOR,
                        lineWidth: 0.5,
                        objectId: otherWall.id,
                        originPoint: { x: otherWall.start.x, y: otherWall.start.y },
                    }, true, "center");
                }
                const alignedYEndEndApprox = otherEndVisible && Math.abs(otherWall.end.y - wallEndY) < toleranceApprox;
                if (alignedYEndEndApprox) {
                    addAlignmentLineWithDedup({
                        id: `align-y-approx-end-end-${otherWall.id}`,
                        hit: { x: 0, y: otherWall.end.y },
                        axis: "y",
                        color: CANVAS_ACCENT_COLOR,
                        lineWidth: 0.5,
                        objectId: otherWall.id,
                        originPoint: { x: otherWall.end.x, y: otherWall.end.y },
                    }, true, "center");
                }

            } else {
                // When dragging start or end handle, align respective point
                // Edge-based alignment for walls is already handled by the block above (lines 115-172)
                // This section only handles center-based alignment for non-walls or when ALT is pressed
                const alignedX =
                    (otherStartVisible && Math.abs(otherWall.start.x - newX) < tolerance) ||
                    (otherEndVisible && Math.abs(otherWall.end.x - newX) < tolerance);
                const alignedY =
                    (otherStartVisible && Math.abs(otherWall.start.y - newY) < tolerance) ||
                    (otherEndVisible && Math.abs(otherWall.end.y - newY) < tolerance);

                if (alignedX) {
                    addAlignmentLineWithDedup({
                        id: `align-x-${otherWall.id}`,
                        hit: { x: newX, y: 0 },
                        axis: "x",
                        color: CANVAS_ACCENT_COLOR,
                        lineWidth: 0.5,
                        objectId: otherWall.id,
                        originPoint: { x: newX, y: newY },
                    }, false, "center");
                }
                const alignedXApproxStart =
                    otherStartVisible && Math.abs(otherWall.start.x - newX) < toleranceApprox
                if (alignedXApproxStart) {
                    addAlignmentLineWithDedup({
                        id: `align-x-approx-start-${otherWall.id}`,
                        hit: { x: otherWall.start.x, y: 0 },
                        axis: "x",
                        color: CANVAS_ACCENT_COLOR,
                        lineWidth: 0.5,
                        objectId: otherWall.id,
                        originPoint: { x: otherWall.start.x, y: otherWall.start.y },
                    }, true, "center");
                }
                const alignedXApproxEnd =
                    otherEndVisible && Math.abs(otherWall.end.x - newX) < toleranceApprox;
                if (alignedXApproxEnd) {
                    addAlignmentLineWithDedup({
                        id: `align-x-approx-end-${otherWall.id}`,
                        hit: { x: otherWall.end.x, y: 0 },
                        axis: "x",
                        color: CANVAS_ACCENT_COLOR,
                        lineWidth: 0.5,
                        objectId: otherWall.id,
                        originPoint: { x: otherWall.end.x, y: otherWall.end.y },
                    }, true, "center");
                }
                if (alignedY) {
                    addAlignmentLineWithDedup({
                        id: `align-y-${otherWall.id}`,
                        hit: { x: 0, y: newY },
                        axis: "y",
                        color: CANVAS_ACCENT_COLOR,
                        lineWidth: 0.5,
                        objectId: otherWall.id,
                        originPoint: { x: newX, y: newY },
                    }, false, "center");
                }
                const alignedYApproxStart =
                    otherStartVisible && Math.abs(otherWall.start.y - newY) < toleranceApprox
                const alignedYApproxEnd =
                    otherEndVisible && Math.abs(otherWall.end.y - newY) < toleranceApprox;
                if (alignedYApproxStart) {
                    addAlignmentLineWithDedup({
                        id: `align-y-approx-start-${otherWall.id}`,
                        hit: { x: 0, y: otherWall.start.y },
                        axis: "y",
                        color: CANVAS_ACCENT_COLOR,
                        lineWidth: 0.5,
                        objectId: otherWall.id,
                        originPoint: { x: otherWall.start.x, y: otherWall.start.y },
                    }, true, "center");
                }
                if (alignedYApproxEnd) {
                    addAlignmentLineWithDedup({
                        id: `align-y-approx-end-${otherWall.id}`,
                        hit: { x: 0, y: otherWall.end.y },
                        axis: "y",
                        color: CANVAS_ACCENT_COLOR,
                        lineWidth: 0.5,
                        objectId: otherWall.id,
                        originPoint: { x: otherWall.end.x, y: otherWall.end.y },
                    }, true, "center");
                }
            }
        } // Close conditional for center-based guidelines
    }

}

function findAlignmentLinesForPoints(
    draggedPoints: AlignmentPoint[],
    otherWall: WallType | SingleLineType | RulerLineType,
) {
    const otherPoints = getAlignmentTargetPoints(otherWall);

    for (const dragPoint of draggedPoints) {
        for (const otherPoint of otherPoints) {
            if (otherPoint.allowX && Math.abs(dragPoint.pos.x - otherPoint.pos.x) < toleranceApprox) {
                addAlignmentLineWithDedup({
                    id: `align-x-edge-${dragPoint.label}-${otherPoint.label}-${otherWall.id}`,
                    hit: { x: otherPoint.pos.x, y: 0 },
                    axis: "x",
                    color: CANVAS_ACCENT_COLOR,
                    lineWidth: 0.5,
                    objectId: otherWall.id,
                    originPoint: { x: otherPoint.pos.x, y: otherPoint.pos.y },
                }, true, "edge");
            }

            if (otherPoint.allowY && Math.abs(dragPoint.pos.y - otherPoint.pos.y) < toleranceApprox) {
                addAlignmentLineWithDedup({
                    id: `align-y-edge-${dragPoint.label}-${otherPoint.label}-${otherWall.id}`,
                    hit: { x: 0, y: otherPoint.pos.y },
                    axis: "y",
                    color: CANVAS_ACCENT_COLOR,
                    lineWidth: 0.5,
                    objectId: otherWall.id,
                    originPoint: { x: otherPoint.pos.x, y: otherPoint.pos.y },
                }, true, "edge");
            }
        }
    }
}

const MAX_ALIGNMENT_LINES = 10; // Maximum number of alignment lines to show
const MAX_DISTANCE_SCREEN_FACTOR = 1.5; // Only show alignment lines within 1.5x the viewport dimension

/**
 * Calculate the shortest distance from a point to a wall segment
 */
function distanceToWall(point: { x: number, y: number }, wall: WallType | SingleLineType | RulerLineType): number {
    // Simple distance to segment
    const l2 = (wall.end.x - wall.start.x) ** 2 + (wall.end.y - wall.start.y) ** 2;
    if (l2 === 0) return Math.sqrt((point.x - wall.start.x) ** 2 + (point.y - wall.start.y) ** 2);

    let t = ((point.x - wall.start.x) * (wall.end.x - wall.start.x) + (point.y - wall.start.y) * (wall.end.y - wall.start.y)) / l2;
    t = Math.max(0, Math.min(1, t));

    const projX = wall.start.x + t * (wall.end.x - wall.start.x);
    const projY = wall.start.y + t * (wall.end.y - wall.start.y);

    return Math.sqrt((point.x - projX) ** 2 + (point.y - projY) ** 2);
}

/**
 * Update alignment lines for a point being dragged.
 * This overload accepts an object directly (useful for area symbols and other non-standard objects).
 */
export const updateAlignmentLinesForObject = (
    object: { id: string; type: string; start: THREE.Vector2; end: THREE.Vector2; centerConnections?: any[] },
    newX: number,
    newY: number,
    dragHandle: string
) => {
    renderStore.clearAlignmentLines();
    // Clear duplicate tracking maps for this drag operation
    addedLinesX.clear();
    addedLinesY.clear();
    // Set current drag point for origin-based prioritization
    setDragPoint(newX, newY);

    const visibleBounds = editorStore.getVisibleBounds?.();

    // Collect all potential targets first
    let targets: (WallType | SingleLineType | RulerLineType)[] = [];

    // Helper to check if object is roughly in viewport (with generous margin)
    const isInViewport = (item: WallType | SingleLineType | RulerLineType) => {
        if (!visibleBounds) return true;

        const margin = Math.max(
            (visibleBounds.xMax - visibleBounds.xMin) * 0.5,
            (visibleBounds.yMax - visibleBounds.yMin) * 0.5
        );

        const minX = Math.min(item.start.x, item.end.x);
        const maxX = Math.max(item.start.x, item.end.x);
        const minY = Math.min(item.start.y, item.end.y);
        const maxY = Math.max(item.start.y, item.end.y);

        return (
            minX < visibleBounds.xMax + margin &&
            maxX > visibleBounds.xMin - margin &&
            minY < visibleBounds.yMax + margin &&
            maxY > visibleBounds.yMin - margin
        );
    };

    // Filter and collect targets
    Array.from(floorplannerStore.wallsMap.values()).forEach((otherWall) => {
        if (
            object.id !== otherWall.id &&
            !object.centerConnections?.some((c: any) => c.id === otherWall.id) &&
            isInViewport(otherWall)
        ) {
            targets.push(otherWall);
        }
    });

    // For single lines and ruler lines, don't filter out connected lines
    // because alignment lines should still be shown even when lines are connected
    // (connections can be broken during drag, and alignment helps position endpoints)
    Array.from(floorplannerStore.singleLinesMap.values()).forEach((otherLine) => {
        if (
            object.id !== otherLine.id &&
            isInViewport(otherLine)
        ) {
            targets.push(otherLine);
        }
    });

    Array.from(floorplannerStore.rulerLinesMap.values()).forEach((otherRuler) => {
        if (
            object.id !== otherRuler.id &&
            isInViewport(otherRuler)
        ) {
            targets.push(otherRuler);
        }
    });

    // Sort targets by distance to the dragged object position
    const dragPoint = { x: newX, y: newY };
    targets.sort((a, b) => {
        const distA = distanceToWall(dragPoint, a);
        const distB = distanceToWall(dragPoint, b);
        return distA - distB;
    });

    // Take only the closest targets
    const closestTargets = targets.slice(0, 20);

    // Generate lines for closest targets
    // Create a pseudo-line object for findAlignmentLines
    const pseudoLine = {
        id: object.id,
        type: object.type === 'area' ? 'singleLine' : object.type, // Treat area as single line for alignment purposes
        start: object.start,
        end: object.end,
    } as SingleLineType;

    for (const target of closestTargets) {
        findAlignmentLines(pseudoLine, target, newX, newY, dragHandle);

        // Stop if we've generated enough unique alignment lines
        if (addedLinesX.size + addedLinesY.size >= MAX_ALIGNMENT_LINES) {
            break;
        }
    }
};

export const updateAlignmentLinesForSelection = (
    corners: THREE.Vector2[],
    center: THREE.Vector2,
    dragPoint: THREE.Vector2,
    excludeIds: Set<string>,
    dragHandle: string = "group"
) => {
    if (editorStore.isAltPressed) {
        const pseudoLine = {
            id: "__group-selection__",
            type: "singleLine",
            start: center,
            end: center,
            centerConnections: Array.from(excludeIds).map((id) => ({ id })),
        } as SingleLineType;
        updateAlignmentLinesForObject(pseudoLine, center.x, center.y, dragHandle);
        return;
    }

    renderStore.clearAlignmentLines();
    addedLinesX.clear();
    addedLinesY.clear();
    setDragPoint(dragPoint.x, dragPoint.y);

    const visibleBounds = editorStore.getVisibleBounds?.();

    const isInViewport = (item: WallType | SingleLineType | RulerLineType) => {
        if (!visibleBounds) return true;

        const margin = Math.max(
            (visibleBounds.xMax - visibleBounds.xMin) * 0.5,
            (visibleBounds.yMax - visibleBounds.yMin) * 0.5
        );

        const minX = Math.min(item.start.x, item.end.x);
        const maxX = Math.max(item.start.x, item.end.x);
        const minY = Math.min(item.start.y, item.end.y);
        const maxY = Math.max(item.start.y, item.end.y);

        return (
            minX < visibleBounds.xMax + margin &&
            maxX > visibleBounds.xMin - margin &&
            minY < visibleBounds.yMax + margin &&
            maxY > visibleBounds.yMin - margin
        );
    };

    let targets: (WallType | SingleLineType | RulerLineType)[] = [];

    Array.from(floorplannerStore.wallsMap.values()).forEach((otherWall) => {
        if (!excludeIds.has(otherWall.id) && isInViewport(otherWall)) {
            targets.push(otherWall);
        }
    });

    Array.from(floorplannerStore.singleLinesMap.values()).forEach((otherLine) => {
        if (!excludeIds.has(otherLine.id) && isInViewport(otherLine)) {
            targets.push(otherLine);
        }
    });

    Array.from(floorplannerStore.rulerLinesMap.values()).forEach((otherRuler) => {
        if (!excludeIds.has(otherRuler.id) && isInViewport(otherRuler)) {
            targets.push(otherRuler);
        }
    });

    targets.sort((a, b) => {
        const distA = distanceToWall({ x: dragPoint.x, y: dragPoint.y }, a);
        const distB = distanceToWall({ x: dragPoint.x, y: dragPoint.y }, b);
        return distA - distB;
    });

    const closestTargets = targets.slice(0, 20);
    const draggedPoints: AlignmentPoint[] = corners.map((corner, index) => ({
        pos: corner,
        label: `corner-${index}`,
        allowX: true,
        allowY: true,
    }));

    for (const target of closestTargets) {
        findAlignmentLinesForPoints(draggedPoints, target);

        if (addedLinesX.size + addedLinesY.size >= MAX_ALIGNMENT_LINES) {
            break;
        }
    }
};

export const updateAlignmentLines = (
    source: string,
    newX: number,
    newY: number,
    dragHandle: string
) => {
    const object = floorplannerStore.findObjectId(source);
    if (!object) {
        return;
    }

    renderStore.clearAlignmentLines();
    // Clear duplicate tracking maps for this drag operation
    addedLinesX.clear();
    addedLinesY.clear();
    // Set current drag point for origin-based prioritization
    setDragPoint(newX, newY);

    const visibleBounds = editorStore.getVisibleBounds?.();

    // Collect all potential targets first
    let targets: (WallType | SingleLineType | RulerLineType)[] = [];

    // Helper to check if object is roughly in viewport (with generous margin)
    // If getVisibleBounds is missing/undefined, default to true (allow all) to prevent crashes
    const isInViewport = (item: WallType | SingleLineType | RulerLineType) => {
        if (!visibleBounds) return true;

        const margin = Math.max(
            (visibleBounds.xMax - visibleBounds.xMin) * 0.5,
            (visibleBounds.yMax - visibleBounds.yMin) * 0.5
        );

        const minX = Math.min(item.start.x, item.end.x);
        const maxX = Math.max(item.start.x, item.end.x);
        const minY = Math.min(item.start.y, item.end.y);
        const maxY = Math.max(item.start.y, item.end.y);

        // Check intersection with expanded viewport
        return (
            minX < visibleBounds.xMax + margin &&
            maxX > visibleBounds.xMin - margin &&
            minY < visibleBounds.yMax + margin &&
            maxY > visibleBounds.yMin - margin
        );
    };

    // Filter and collect targets
    Array.from(floorplannerStore.wallsMap.values()).forEach((otherWall) => {
        if (
            object.id !== otherWall.id &&
            !object.centerConnections?.some((c) => c.id === otherWall.id) &&
            isInViewport(otherWall)
        ) {
            targets.push(otherWall);
        }
    });

    // For single lines and ruler lines, don't filter out connected lines
    // because alignment lines should still be shown even when lines are connected
    // (connections can be broken during drag, and alignment helps position endpoints)
    Array.from(floorplannerStore.singleLinesMap.values()).forEach((otherLine) => {
        if (
            object.id !== otherLine.id &&
            isInViewport(otherLine)
        ) {
            targets.push(otherLine);
        }
    });

    Array.from(floorplannerStore.rulerLinesMap.values()).forEach((otherRuler) => {
        if (
            object.id !== otherRuler.id &&
            isInViewport(otherRuler)
        ) {
            targets.push(otherRuler);
        }
    });

    // Sort targets by distance to the dragged object position
    const dragPoint = { x: newX, y: newY };
    targets.sort((a, b) => {
        const distA = distanceToWall(dragPoint, a);
        const distB = distanceToWall(dragPoint, b);
        return distA - distB;
    });

    // Take only the closest targets
    // We increase the limit slightly because each target might produce multiple lines,
    // but this ensures we don't process hundreds of walls
    const closestTargets = targets.slice(0, 20);

    // Generate lines for closest targets
    let linesGenerated = 0;

    for (const target of closestTargets) {
        findAlignmentLines(object, target, newX, newY, dragHandle);
        linesGenerated++;

        // Stop if we've generated enough unique alignment lines (checking the deduplication maps)
        if (addedLinesX.size + addedLinesY.size >= MAX_ALIGNMENT_LINES) {
            break;
        }
    }
};
